import React from "react";
import { Building2, CalendarPlus, CheckCircle2, Palette } from "lucide-react";
import { Button } from "../ui";
import Card from "../ui/Card";
import { FieldHint, FORM_STYLE_CARD_CLASS, SectionTitle } from "../createEvent/CreateEventFormUi";

/**
 * Activation hub — get an event or school live, then optional theme / finish.
 */
export default function HostOnboardingLoopStep({
  schoolSlug,
  activated = false,
  busy,
  onCreateEvent,
  onCreateSchool,
  onContinueToTheme,
  onFinish,
}) {
  const isLive = Boolean(activated || schoolSlug);

  if (isLive) {
    return (
      <div className="space-y-4" data-testid="host-onboarding-loop">
        <Card className={FORM_STYLE_CARD_CLASS} data-testid="host-onboarding-live">
          <SectionTitle>
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0 text-ink" strokeWidth={2} aria-hidden />
              {schoolSlug ? "School is live" : "Event is live"}
            </span>
          </SectionTitle>
          <FieldHint className="text-[13px]">
            That’s the hard part. Share your link, pick a look, or add more later.
          </FieldHint>

          <div className="mt-5 space-y-3">
            <Button
              type="button"
              variant="primary"
              size="lg"
              disabled={busy}
              onClick={onContinueToTheme}
              data-testid="host-onboarding-pick-theme"
              className="w-full justify-center gap-2 min-h-[48px]"
            >
              <Palette className="w-4 h-4 shrink-0" strokeWidth={2} aria-hidden />
              Pick a look
            </Button>

            {!schoolSlug ? (
              <button
                type="button"
                disabled={busy}
                onClick={onCreateSchool}
                data-testid="host-onboarding-create-school"
                className="w-full text-left rounded-lg2 border border-black/[0.1] bg-surface p-4 min-h-[44px] hover:border-ink/25 motion-safe:transition-colors motion-safe:duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink/30 focus-visible:outline-offset-2 disabled:opacity-50"
              >
                <span className="inline-flex items-center gap-2 font-ui font-semibold text-[15px]">
                  <Building2 className="w-4 h-4 shrink-0" strokeWidth={2} aria-hidden />
                  Add a school
                </span>
                <span className="mt-1 block font-ui text-[13px] text-ink/70 leading-snug">
                  Optional — for classes and memberships.
                </span>
              </button>
            ) : (
              <Button
                type="button"
                variant="secondary"
                size="lg"
                disabled={busy}
                onClick={onCreateEvent}
                data-testid="host-onboarding-create-event"
                className="w-full justify-center gap-2 min-h-[48px]"
              >
                <CalendarPlus className="w-4 h-4 shrink-0" strokeWidth={2} aria-hidden />
                Create an event too
              </Button>
            )}
          </div>

          <div className="mt-5 flex justify-center border-t border-black/[0.08] pt-4">
            <Button
              type="button"
              variant="link"
              size="sm"
              disabled={busy}
              onClick={onFinish}
              data-testid="host-onboarding-loop-done"
              className="min-h-[44px]"
            >
              {schoolSlug ? "Open school admin →" : "I’m done — go to events →"}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="host-onboarding-loop">
      <Card className={FORM_STYLE_CARD_CLASS}>
        <SectionTitle>Publish something guests can open</SectionTitle>
        <FieldHint className="text-[13px]">
          An event link is usually fastest — the guest list is the product. A school works too if you run classes.
        </FieldHint>

        <div className="mt-5 space-y-3">
          <Button
            type="button"
            variant="primary"
            size="lg"
            disabled={busy}
            onClick={onCreateEvent}
            data-testid="host-onboarding-create-event"
            className="w-full justify-center gap-2 min-h-[48px]"
          >
            <CalendarPlus className="w-4 h-4 shrink-0" strokeWidth={2} aria-hidden />
            Create an event
          </Button>
          <p className="font-ui text-[13px] text-ink/70 leading-snug text-pretty px-0.5">
            Social, party, or workshop — share the invite on WhatsApp.
          </p>

          <button
            type="button"
            disabled={busy}
            onClick={onCreateSchool}
            data-testid="host-onboarding-create-school"
            className="w-full text-left rounded-lg2 border border-black/[0.1] bg-surface p-4 min-h-[44px] hover:border-ink/25 motion-safe:transition-colors motion-safe:duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink/30 focus-visible:outline-offset-2 disabled:opacity-50 disabled:hover:border-black/[0.1]"
          >
            <span className="inline-flex items-center gap-2 font-ui font-semibold text-[15px]">
              <Building2 className="w-4 h-4 shrink-0" strokeWidth={2} aria-hidden />
              Create a school
            </span>
            <span className="mt-1 block font-ui text-[13px] text-ink/70 leading-snug">
              Name and city now. Payments are optional afterward.
            </span>
          </button>
        </div>

        <div className="mt-5 flex justify-center border-t border-black/[0.08] pt-4">
          <Button
            type="button"
            variant="link"
            size="sm"
            disabled={busy}
            onClick={onContinueToTheme}
            data-testid="host-onboarding-loop-skip"
            className="min-h-[44px]"
          >
            Skip for now →
          </Button>
        </div>
      </Card>
    </div>
  );
}
